import {Box, HStack, Spacer, Text, Tooltip, VStack} from '@chakra-ui/react';
import {State} from '@synako/halitrek';
import {
  DisponibiliteArticleDesc,
  ItemCatalogue2Desc,
  LigneDePanierDesc,
} from 'api-types/ffconnect-hal';
import {LigneProblem} from 'api-types/ffconnect';
import {ArticleBlock} from '../ArticleBlock';
import {ArticleBlockMarketplace} from '../ArticleBlockMarketplace';
import {RemplacerErrorButton} from '../ErrorButton/RemplacerErrorButton';
import {DiffererErrorButton} from '../ErrorButton/DiffererErrorButton';
import {SupprimerErrorButton} from '../ErrorButton/SupprimerErrorButton';
import {AjusterErrorButton} from '../ErrorButton/AjusterErrorButton';
import {
  isItemStateCatalogueMarketplace,
  useLigneDePanierCommands,
  usePanierAffichageBoutonsErreurs,
} from 'ff-common';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {ModaleErreurDisponibilite} from '@/domains/cadencier/components/ModaleErreurDisponibilite.tsx';
import {useMarketplaceBrandColors} from '@/hooks/marketplace/useMarketplaceBrandColors';

export interface ProduitPanierErrorCommunProps {
  context: 'modale' | 'panier';
  ligneDePanier: State<LigneDePanierDesc>;
  idError: string;
  probleme?: LigneProblem;
  dispo?: State<DisponibiliteArticleDesc>;
  onClose?: () => void;
  submittingOrRefreshing?: boolean;
  multipleCommandeClient?: number;
  onProductClick?: (code_article: string) => void;
  itemState?: State<ItemCatalogue2Desc>;
}

export const ProduitPanierErrorCommun: React.FC<
  ProduitPanierErrorCommunProps
> = (props) => {
  const {
    context,
    idError,
    probleme,
    dispo,
    ligneDePanier,
    onClose,
    submittingOrRefreshing,
    multipleCommandeClient,
    onProductClick,
    itemState,
  } = props;
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const commands = useLigneDePanierCommands(
    ligneDePanier,
    textToast,
    onError,
    ModaleErreurDisponibilite,
  );
  const marketplaceId =
    itemState && isItemStateCatalogueMarketplace(itemState)
      ? itemState.data.marketplace_id
      : undefined;
  const {couleur: primaryColor} = useMarketplaceBrandColors(marketplaceId);

  const articleBlock =
    itemState && isItemStateCatalogueMarketplace(itemState) ? (
      <ArticleBlockMarketplace itemState={itemState} />
    ) : (
      <ArticleBlock
        code_article={ligneDePanier.data.code_article}
        dispo={ligneDePanier.follow('disponibilite_article')}
        libelleFallback={ligneDePanier.data.libelle}
        articleFallback={
          ligneDePanier.hasOneLink('article')
            ? ligneDePanier.follow('article')
            : undefined
        }
        onProductClick={onProductClick}
        // afficher_bouton_dlc_stock // Réservé au mode commercial
      />
    );

  const {buttons, message} = usePanierAffichageBoutonsErreurs({
    multipleCommandeClient,
    isMarketplace: itemState
      ? isItemStateCatalogueMarketplace(itemState)
      : false,
    ligneDePanier,
    probleme,
    dispo,
    tooltip: (label, message) => (
      <Tooltip
        label={label}
        placement='top'
      >
        <Text>{message}</Text>
      </Tooltip>
    ),
    RemplacerErrorButton: (
      <RemplacerErrorButton
        commands={commands}
        ligneDePanier={ligneDePanier}
        submittingOrRefreshing={submittingOrRefreshing}
        onProductClick={onProductClick}
        primaryColor={primaryColor}
      />
    ),
    DiffererErrorButton: (
      <DiffererErrorButton
        commands={commands}
        ligneDePanier={ligneDePanier}
        submittingOrRefreshing={submittingOrRefreshing}
        primaryColor={primaryColor}
      />
    ),
    SupprimerErrorButton: (
      <SupprimerErrorButton
        commands={commands}
        onClose={() => {
          onClose?.();
        }}
        code_article={ligneDePanier.data.code_article}
        submittingOrRefreshing={submittingOrRefreshing}
        primaryColor={primaryColor}
      />
    ),
    AjusterErrorButton: (quantiteMax) => (
      <AjusterErrorButton
        ligneDePanier={ligneDePanier}
        commands={commands}
        quantiteMaximale={quantiteMax}
        onClose={() => {
          onClose?.();
        }}
        submittingOrRefreshing={submittingOrRefreshing}
        primaryColor={primaryColor}
      />
    ),
  });

  return (
    <VStack
      id={idError}
      p={{base: '0', md: '20px'}}
      borderTop={context === 'modale' ? '0' : '1px'}
      borderColor='gray.light'
      _notLast={{borderBottom: '0'}}
      background='white'
      width='100%'
      alignItems='stretch'
      position='relative'
      display='flex'
      mt={{base: '10px', md: '0'}}
      pt={{base: '10px', md: context === 'modale' ? '0' : '10px'}}
    >
      {/* Desktop */}
      <HStack
        display={{base: 'none', md: 'flex'}}
        width='100%'
        alignItems='start'
        justify='end'
        flexWrap='wrap'
        gap='0'
        pt='0px'
        flex='1'
        opacity={submittingOrRefreshing ? 0.3 : 1}
      >
        {articleBlock}

        <Spacer />

        <HStack
          justifyContent='end'
          alignItems='start'
          justifySelf='end'
          w='100%'
        >
          <VStack alignItems='end'>
            <HStack>
              <Box
                p='1.5'
                px='2.5'
                borderRadius='8px'
                backgroundColor='red.warning_bg'
                color='red.fail'
                fontSize='xs'
              >
                {message}
              </Box>
            </HStack>
            <HStack justifyContent={{base: 'start', lg: 'end'}}>
              {buttons}
            </HStack>
          </VStack>
        </HStack>
      </HStack>

      {/* Mobile */}
      <VStack
        display={{base: 'block', md: 'none'}}
        width='100%'
        alignItems='start'
        justify='end'
      >
        {articleBlock}
        <Spacer />
        <Box
          p='1.5'
          px='2.5'
          borderRadius='8px'
          backgroundColor='red.warning_bg'
          color='red.fail'
          fontSize='xs'
          mb='1'
        >
          {message}
        </Box>
        <HStack
          justify={{base: 'center', md: 'space-between'}}
          sx={{flex: 1}}
          mt='10px'
        >
          {buttons}
        </HStack>
      </VStack>
    </VStack>
  );
};
